import { CommonErrorType, DialogType } from '../../common/enums.js';
import {
  companyCreditLimitExceededEmployeeMsg,
  corporateOrExtensionNumberIsNotAvailableMsg,
  customerServiceNumberLabelMsg,
  customerServiceNumberOpenLongMsg,
  customerServiceNumberPriceMsg,
  employeeCreditCheckFailedMsg,
  ifProblemPersistsMsg,
  orderCouldNotBeProcessedMsg,
  problemWithProcessingOrderMsg,
  punchoutDeviceLimitMsg,
  subscriptionClosedByCreditMsg,
  subscriptionClosedMsg,
  systemErrorOccurredMsg,
  t,
  vakioConfigurationErrorMsg,
} from '../../common/i18n/index.js';
import { getErrorsFromCheckout } from '../../common/utils/checkoutUtils.js';
import { getErrorsFromUpsertDeliveryAddress } from '../../common/utils/errorUtils.js';
import { ONE_TIME_DEVICE_CHANGE_NOT_ALLOWED } from '../../common/utils/subscriptionUtils.js';
import { getErrorsFromSupportCaseDialog } from '../../components/SupportCaseDialog/SupportCaseDialog.js';
import { getErrorsFromUpdateUserDetails } from '../../components/UserDetails/userDetailsUtils.js';
import { CreditCheckDecision } from '../../generated/api/models.js';
import { closeDialog, setDialogSubmitFailed, updateDialog } from '../../public/site/dialogStore.js';
import { BlockedErrorTypes, PbxErrorTypes, TypeKeys } from '../actions/index.js';
import type { ActionTypes } from '../actions/index.js';
import type { Middleware } from 'redux';

function verifyBlockType(blockType: string): void {
  let body = t.RVSV('Unknown error.');
  switch (blockType) {
    case BlockedErrorTypes.COLLECTION_INITIATED: {
      body = t.VMT9(
        'Subscription cannot be activated due to unpaid invoices. Please check the status of the invoices.'
      );
      break;
    }
    case BlockedErrorTypes.CREDIT_INITIATED: {
      body = t.P5F7('Subscription cannot be activated through OmaElisa.');
      break;
    }
  }
  updateDialog({
    params: {
      body,
      header: t.M59G('An error occurred while activating the subscription'),
      type: DialogType.GENERIC_INFO_DIALOG,
    },
    submitInProgress: false,
    visible: true,
  });
}

enum PunchoutOrderFailure {
  DEVICE_LIMIT_EXCEEDED = 'DEVICE_LIMIT_EXCEEDED',
}

// Bridge: translates Redux actions dispatched by other, not-yet-migrated slices into dialogStore calls.
// Mirrors the (now removed) dialogReducer.ts case-by-case so both migrated and not-yet-migrated callers
// see the same dialog state via useDialog().
/**
 * TRANSITIONAL. Translates Redux actions from feature slices that still live in Redux into
 * `dialogStore` calls, so those slices keep driving dialogs after the dialog slice itself was
 * removed (OFI-65066).
 *
 * This middleware is not the dialog API — new code must call `dialogStore` directly. Every case
 * here belongs to a feature slice that has not been migrated away from Redux yet:
 *   - support cases
 *   - contacts
 *   - online order + credit check
 *   - punchout
 *   - employee onboarding
 *   - subscription actions
 *
 * As each of those slices moves off Redux it must delete its own cases below. Once all are gone
 * (CLEAR_STATE last, since it is shared), this file can be deleted and
 * `configureStoreWithMiddleware` stops registering it.
 */
export const dialogBridgeMiddleware: Middleware = () => next => (action: ActionTypes) => {
  const result = next(action);

  switch (action.type) {
    case TypeKeys.ADD_SUPPORT_CASE: {
      const errors = getErrorsFromSupportCaseDialog(action.supportCase);
      updateDialog({ errors, submitInProgress: !errors });
      break;
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED: {
      const header = t.SKDZ(vakioConfigurationErrorMsg);
      let body = t.OKCS(ifProblemPersistsMsg);
      if (action.errors?.[0]) {
        const error = action.errors[0];
        if (error.type === (CommonErrorType.BAD_REQUEST || PbxErrorTypes.NUMBER_NOT_AVAILABLE)) {
          body = t.UR1U(corporateOrExtensionNumberIsNotAvailableMsg);
        } else {
          body = t.JCJF(systemErrorOccurredMsg);
        }
      }
      updateDialog({
        params: { header, body, type: DialogType.GENERIC_INFO_DIALOG },
        visible: true,
      });
      break;
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FAILED: {
      // only in case of one-time-charge device
      if (
        action.errors?.[0].type === CommonErrorType.FORBIDDEN &&
        action.errors?.[0]?.message.includes(ONE_TIME_DEVICE_CHANGE_NOT_ALLOWED)
      ) {
        updateDialog({
          params: {
            header: t.RQMS('Editing details is not possible'),
            body: t.I2EO('Editing details for one-time-fee products is not possible'),
            type: DialogType.GENERIC_INFO_DIALOG,
          },
          visible: true,
        });
      } else {
        setDialogSubmitFailed();
      }
      break;
    }

    case TypeKeys.POLL_FOR_CREDIT_CHECK_FAILED: {
      // Cannot know the state of the credit check -> cannot know if the order is processed or not
      updateDialog({
        errors: [
          {
            message: t.STWG(
              problemWithProcessingOrderMsg,
              customerServiceNumberLabelMsg,
              t.HQWW(customerServiceNumberPriceMsg),
              t.HQWV(customerServiceNumberOpenLongMsg)
            ),
            type: CommonErrorType.FORBIDDEN,
          },
        ],
      });
      break;
    }

    case TypeKeys.SUBMIT_ONLINE_ORDER: {
      if (
        getErrorsFromUpsertDeliveryAddress(action.deliveryAddress, action.isCompanyNameRequired) ||
        getErrorsFromCheckout(action.approverContact, action.personBillingAddress, action.personBillingEmail) ||
        (action.contactPerson &&
          getErrorsFromUpdateUserDetails(
            action.contactPerson,
            action.customerType,
            action.contactPersonValidationErrors
          ))
      ) {
        break;
      }
      updateDialog({
        params: { type: DialogType.SUBMIT_ORDER },
        visible: true,
        errors: undefined,
      });
      break;
    }

    case TypeKeys.SUBMIT_ONLINE_ORDER_FAILED: {
      if (action.errors?.[0].source?.error?.message.includes(CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION)) {
        updateDialog({
          errors: [
            {
              message: CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION,
              type: CommonErrorType.FORBIDDEN,
            },
          ],
        });
        break;
      }
      updateDialog({
        errors: [
          {
            message: t.MOF8(
              orderCouldNotBeProcessedMsg,
              customerServiceNumberLabelMsg,
              t.HQWV(customerServiceNumberOpenLongMsg),
              t.HQWW(customerServiceNumberPriceMsg)
            ),
            type: CommonErrorType.SYSTEM,
          },
        ],
      });
      break;
    }

    case TypeKeys.CHANGE_ADDONS:
    case TypeKeys.SUSPEND_SUBSCRIPTION:
    case TypeKeys.RESUME_SUBSCRIPTION: {
      updateDialog({ errors: undefined, submitInProgress: true });
      break;
    }

    case TypeKeys.CLEAR_STATE:
    case TypeKeys.CHANGE_ADDONS_FULFILLED:
    case TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED:
    case TypeKeys.SUSPEND_SUBSCRIPTION_FULFILLED:
    case TypeKeys.RESUME_SUBSCRIPTION_FULFILLED:
    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED:
    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED: {
      closeDialog();
      break;
    }

    case TypeKeys.ADD_SUPPORT_CASE_FULFILLED: {
      if (action.showInfoDialog) {
        updateDialog({ params: { type: DialogType.SUPPORT_CASE_SENT_INFO_DIALOG } });
      } else {
        closeDialog();
      }
      break;
    }

    case TypeKeys.ADD_SUPPORT_CASE_FAILED:
    case TypeKeys.CHANGE_ADDONS_FAILED:
    case TypeKeys.POST_EPP_REDEEM_TERMINATE_FAILED:
    case TypeKeys.SUSPEND_SUBSCRIPTION_FAILED:
    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FAILED: {
      setDialogSubmitFailed();
      break;
    }

    case TypeKeys.POLL_FOR_CREDIT_CHECK_V2_FULFILLED: {
      switch (action.response.decision) {
        case CreditCheckDecision.PASS: {
          closeDialog();
          break;
        }
        case CreditCheckDecision.COMPANY_CREDIT_LIMIT_EXCEEDED: {
          updateDialog({
            errors: [{ message: CreditCheckDecision.COMPANY_CREDIT_LIMIT_EXCEEDED, type: CommonErrorType.FORBIDDEN }],
          });
          break;
        }
        case CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION: {
          updateDialog({
            errors: [
              { message: CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION, type: CommonErrorType.FORBIDDEN },
            ],
          });
          break;
        }
        case CreditCheckDecision.INTERNAL_ERROR: {
          updateDialog({
            errors: [
              {
                message: t.MOF8(
                  orderCouldNotBeProcessedMsg,
                  customerServiceNumberLabelMsg,
                  t.HQWV(customerServiceNumberOpenLongMsg),
                  t.HQWW(customerServiceNumberPriceMsg)
                ),
                type: CommonErrorType.SYSTEM,
              },
            ],
          });
          break;
        }
        case CreditCheckDecision.DECLINED_RECOMMEND_CARD_PAYMENT: {
          updateDialog({
            errors: [{ message: CreditCheckDecision.DECLINED_RECOMMEND_CARD_PAYMENT, type: CommonErrorType.FORBIDDEN }],
          });
          break;
        }
        default: {
          updateDialog({
            errors: [
              { message: CreditCheckDecision.COMPANY_NEGATIVE_CREDIT_DECISION, type: CommonErrorType.FORBIDDEN },
            ],
          });
          break;
        }
      }
      break;
    }

    case TypeKeys.INITIATE_EMPLOYEE_ONBOARDING: {
      if (action.validationErrors?.length) {
        updateDialog({ errors: action.validationErrors });
      } else {
        updateDialog({ errors: undefined, submitInProgress: true });
      }
      break;
    }

    case TypeKeys.RESUME_SUBSCRIPTION_FAILED: {
      if (
        action.errors?.[0].source?.statusCode === 409 &&
        (action.blockType === BlockedErrorTypes.CREDIT_INITIATED ||
          action.blockType === BlockedErrorTypes.COLLECTION_INITIATED)
      ) {
        updateDialog({
          params: {
            body: t.JIXE(
              subscriptionClosedByCreditMsg,
              customerServiceNumberLabelMsg,
              t.HQWV(customerServiceNumberOpenLongMsg)
            ),
            header: t.R28M(subscriptionClosedMsg),
            type: DialogType.GENERIC_INFO_DIALOG,
          },
          visible: true,
          submitInProgress: false,
        });
        break;
      }
      if (action.blockType) {
        verifyBlockType(action.blockType);
        break;
      }
      setDialogSubmitFailed();
      break;
    }

    case TypeKeys.SUBMIT_PUNCHOUT_ORDER_FAILED: {
      const errorMsg = action.errors?.[0].source?.error?.message;
      const statusCode = action.errors?.[0].source?.statusCode;
      if (errorMsg?.includes(CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION)) {
        updateDialog({
          errors: [
            {
              message: t.ZE11(employeeCreditCheckFailedMsg),
              type: CommonErrorType.FORBIDDEN,
              category: CreditCheckDecision.EMPLOYEE_NEGATIVE_CREDIT_DECISION,
            },
          ],
        });
      } else if (
        errorMsg?.includes(CreditCheckDecision.COMPANY_NEGATIVE_CREDIT_DECISION) ||
        errorMsg?.includes(CreditCheckDecision.COMPANY_CREDIT_LIMIT_EXCEEDED) ||
        errorMsg?.includes(CreditCheckDecision.DECLINED)
      ) {
        updateDialog({
          errors: [{ message: t.GCDV(companyCreditLimitExceededEmployeeMsg), type: CommonErrorType.FORBIDDEN }],
        });
      } else if (errorMsg?.includes(PunchoutOrderFailure.DEVICE_LIMIT_EXCEEDED)) {
        updateDialog({
          errors: [
            {
              message: t.HC61(punchoutDeviceLimitMsg),
              type: statusCode === 400 ? CommonErrorType.BAD_REQUEST : CommonErrorType.FORBIDDEN,
            },
          ],
        });
      } else {
        updateDialog({
          errors: [
            {
              message: t.MOF8(
                orderCouldNotBeProcessedMsg,
                customerServiceNumberLabelMsg,
                t.HQWV(customerServiceNumberOpenLongMsg),
                t.HQWW(customerServiceNumberPriceMsg)
              ),
              type: CommonErrorType.SYSTEM,
            },
          ],
        });
      }
      break;
    }

    case TypeKeys.DUPLICATE_CONTACTS_CHECK_FULFILLED: {
      if (action.response.duplicateContacts && action.response.duplicateContacts.length > 0) {
        updateDialog({ params: { type: DialogType.DUPLICATE_CONTACTS_CHECK } });
      }
      break;
    }

    default:
      break;
  }

  return result;
};
